// Pure month-grid math for the jobs calendar (ADV-786). Native Date only — a
// Sunday-start month grid needs no date library, and keeping it pure makes the
// leading/trailing-day and bucketing logic unit-testable. (Slice 2 introduces
// date-fns alongside the week view, where its week math earns its place; the
// `WEEK_STARTS_ON` constant below is the single flip-point per decision C1.)

/** Sunday-start, fixed for v1 (decision C1). 0 = Sunday. Module-local — slice 2
 * exports it as the single flip-point when the week view + a start-day setting
 * land. */
const WEEK_STARTS_ON = 0;

interface CalendarDay {
  /** Local date key `YYYY-MM-DD` — the bucket key jobs join on. */
  key: string;
  /** Day-of-month number shown in the cell. */
  day: number;
  /** Whether the cell belongs to the displayed month (vs leading/trailing). */
  inMonth: boolean;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Local `YYYY-MM-DD` key for a Date (no UTC shift — calendars are local). */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Build the 6×7 (42-cell) month grid for `year`/`month` (0-based month),
 * Sunday-start, with leading days from the previous month and trailing days
 * from the next so every week row is full. A fixed 6-row grid keeps the grid
 * height stable across months (no layout jump when navigating).
 */
export function buildMonthGrid(year: number, month: number): CalendarDay[] {
  const firstOfMonth = new Date(year, month, 1);
  const leading = (firstOfMonth.getDay() - WEEK_STARTS_ON + 7) % 7;
  const start = new Date(year, month, 1 - leading);

  const cells: CalendarDay[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + i,
    );
    cells.push({
      key: dateKey(d),
      day: d.getDate(),
      inMonth: d.getMonth() === month,
    });
  }
  return cells;
}

/** Weekday header labels in display order (Sunday-start). */
export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "June 2026" — the absolute month label (§4.3, never relative/`06/2026`). */
export function monthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

/** Add `delta` months to a {year, month}, normalizing the rollover. */
export function addMonths(
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

/** Add `delta` days to a date, returning a new local Date. */
export function addDays(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + delta);
}

/** "Tuesday, June 10, 2026" — the absolute single-day label for the day view
 * (§4.3, never relative). The grid/day header IS the date context. */
export function dayLabel(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** Count items whose date keys fall in the given 0-based year/month. */
export function monthJobCount<T>(
  byDate: Map<string, T[]>,
  year: number,
  month: number,
): number {
  const prefix = `${year}-${pad(month + 1)}`;
  let count = 0;
  for (const [key, bucket] of byDate) {
    if (key.slice(0, 7) === prefix) count += bucket.length;
  }
  return count;
}

/**
 * True when a production span [start, end] (YYYY-MM-DD strings) overlaps any
 * day of the given month. The month grid renders spanning jobs even when they
 * have no `scheduled_date` bucket in the month, so the empty-month cue must
 * consult this too — otherwise a month showing only span bars is wrongly
 * announced as empty.
 */
export function spanIntersectsMonth(
  start: string | null | undefined,
  end: string | null | undefined,
  year: number,
  month: number,
): boolean {
  if (!start || !end) return false;
  const s = start.slice(0, 10);
  const e = end.slice(0, 10);
  const lastDay = new Date(year, month + 1, 0).getDate();
  const monthStart = `${year}-${pad(month + 1)}-01`;
  const monthEnd = `${year}-${pad(month + 1)}-${pad(lastDay)}`;
  return s <= monthEnd && e >= monthStart;
}

/**
 * Find the month (by minimum |month distance| from `from`) that has ≥1 job.
 * Returns `null` when the map is empty. Ties resolve to the earlier month.
 */
/**
 * All `YYYY-MM` month keys a production span [start, end] (YYYY-MM-DD strings)
 * covers, inclusive. Used so the empty-month jump target can consider months
 * that are visibly populated by span bars but have no `scheduled_date` bucket.
 */
export function monthsInSpan(
  start: string | null | undefined,
  end: string | null | undefined,
): string[] {
  if (!start || !end) return [];
  const s = start.slice(0, 10);
  const e = end.slice(0, 10);
  if (s > e) return [];
  let year = parseInt(s.slice(0, 4), 10);
  let month = parseInt(s.slice(5, 7), 10) - 1;
  const endYear = parseInt(e.slice(0, 4), 10);
  const endMonth = parseInt(e.slice(5, 7), 10) - 1;
  if ([year, month, endYear, endMonth].some(Number.isNaN)) return [];
  const out: string[] = [];
  while (year < endYear || (year === endYear && month <= endMonth)) {
    out.push(`${year}-${pad(month + 1)}`);
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return out;
}

export function nearestPopulatedMonth<T>(
  byDate: Map<string, T[]>,
  from: { year: number; month: number },
  extraMonthKeys?: Iterable<string>,
): { year: number; month: number } | null {
  const populated = new Map<string, { year: number; month: number }>();
  const addKey = (key: string) => {
    const ym = key.slice(0, 7);
    if (ym.length !== 7 || populated.has(ym)) return;
    const yr = parseInt(ym.slice(0, 4), 10);
    const mo = parseInt(ym.slice(5, 7), 10) - 1;
    if (Number.isNaN(yr) || Number.isNaN(mo)) return;
    populated.set(ym, { year: yr, month: mo });
  };
  for (const key of byDate.keys()) addKey(key);
  if (extraMonthKeys) for (const key of extraMonthKeys) addKey(key);
  if (populated.size === 0) return null;

  const fromTotal = from.year * 12 + from.month;
  let best: { year: number; month: number } | null = null;
  let bestDist = Infinity;

  for (const candidate of populated.values()) {
    const dist = Math.abs(candidate.year * 12 + candidate.month - fromTotal);
    if (
      dist < bestDist ||
      (dist === bestDist &&
        best !== null &&
        candidate.year * 12 + candidate.month < best.year * 12 + best.month)
    ) {
      best = candidate;
      bestDist = dist;
    }
  }

  return best;
}

/**
 * Bucket items by their local date key. `getDate` extracts the item's date
 * string (e.g. a job's `scheduled_date`); items with no usable date are
 * dropped. Returns a Map keyed by `YYYY-MM-DD`.
 */
export function bucketByDate<T>(
  items: T[],
  getDate: (item: T) => string | null | undefined,
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const raw = getDate(item);
    if (!raw) continue;
    // Normalize a full ISO timestamp or a bare date to the `YYYY-MM-DD` key.
    const key = raw.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) continue;
    const bucket = map.get(key) ?? [];
    bucket.push(item);
    map.set(key, bucket);
  }
  return map;
}

// --- Span-bar math (ADV-1351) ---

const MS_PER_DAY = 86_400_000;

/** Parse a YYYY-MM-DD string into a noon-anchored local Date (DST-safe). */
function parseLocalDateAtNoon(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const key = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d, 12);
}

interface DateRange {
  start: string | null | undefined;
  end: string | null | undefined;
}

interface SpanSegment {
  weekIndex: number;
  startCol: number;
  span: number;
  isStart: boolean;
  isEnd: boolean;
  clippedStart: boolean;
  clippedEnd: boolean;
}

/**
 * Decompose a date range into per-week-row segments using the same grid
 * arithmetic as `buildMonthGrid`. DST-safe (noon-anchored, Math.round).
 * Returns [] for null/malformed endpoints, inverted ranges, or ranges fully
 * outside the 42-cell grid.
 */
export function spanSegments(
  range: DateRange,
  year: number,
  month: number,
): SpanSegment[] {
  const startDate = parseLocalDateAtNoon(range.start);
  const endDate = parseLocalDateAtNoon(range.end);
  if (!startDate || !endDate) return [];

  const firstOfMonth = new Date(year, month, 1);
  const leading = (firstOfMonth.getDay() - WEEK_STARTS_ON + 7) % 7;
  // Noon-anchored grid origin — matches buildMonthGrid's start calculation.
  const gridStart = new Date(year, month, 1 - leading, 12);

  const toIdx = (d: Date) =>
    Math.round((d.getTime() - gridStart.getTime()) / MS_PER_DAY);

  const rawStartIdx = toIdx(startDate);
  const rawEndIdx = toIdx(endDate);

  if (rawEndIdx < rawStartIdx) return [];
  if (rawEndIdx < 0 || rawStartIdx > 41) return [];

  const clippedStart = rawStartIdx < 0;
  const clippedEnd = rawEndIdx > 41;
  const startIdx = Math.max(0, rawStartIdx);
  const endIdx = Math.min(41, rawEndIdx);

  const firstWeek = Math.floor(startIdx / 7);
  const lastWeek = Math.floor(endIdx / 7);

  const segments: SpanSegment[] = [];
  for (let w = firstWeek; w <= lastWeek; w++) {
    const rowStart = Math.max(startIdx, w * 7);
    const rowEnd = Math.min(endIdx, (w + 1) * 7 - 1);
    segments.push({
      weekIndex: w,
      startCol: rowStart % 7,
      span: rowEnd - rowStart + 1,
      isStart: w === firstWeek && !clippedStart,
      isEnd: w === lastWeek && !clippedEnd,
      clippedStart: w === firstWeek && clippedStart,
      clippedEnd: w === lastWeek && clippedEnd,
    });
  }
  return segments;
}

export interface JobSpan {
  id: string;
  start: string | null | undefined;
  end: string | null | undefined;
  crew?: string | null;
  crewId?: string | null;
  status: string;
  canceled: boolean;
  hasConflict: boolean;
}

export interface WeekSegment {
  key: string;
  id: string;
  label: string;
  weekIndex: number;
  startCol: number;
  span: number;
  lane: number;
  continuesLeft: boolean;
  continuesRight: boolean;
  status: string;
  canceled: boolean;
  hasConflict: boolean;
}

/** Assign lanes greedily: sort by startCol (tiebreak: longer span, then id). */
function assignLanes(segments: Omit<WeekSegment, "lane">[]): WeekSegment[] {
  const sorted = [...segments].sort((a, b) => {
    if (a.startCol !== b.startCol) return a.startCol - b.startCol;
    if (b.span !== a.span) return b.span - a.span;
    return a.id < b.id ? -1 : 1;
  });

  // Track the last-used endCol per lane (inclusive).
  const laneEnds: number[] = [];
  const result: WeekSegment[] = [];

  for (const seg of sorted) {
    const endCol = seg.startCol + seg.span - 1;
    let lane = laneEnds.findIndex((le) => le < seg.startCol);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(endCol);
    } else {
      laneEnds[lane] = endCol;
    }
    result.push({ ...seg, lane });
  }
  return result;
}

/**
 * Build per-week-row segment lists from a set of job spans, including greedy
 * lane assignment. Inverted ranges (start > end) produce no segments.
 */
export function buildWeekSegments(
  year: number,
  month: number,
  spans: JobSpan[],
): {
  weeks: { weekIndex: number; segments: WeekSegment[]; laneCount: number }[];
} {
  const byWeek = new Map<number, Omit<WeekSegment, "lane">[]>();

  for (const span of spans) {
    const rawSegs = spanSegments(
      { start: span.start, end: span.end },
      year,
      month,
    );
    for (const seg of rawSegs) {
      const continuesLeft = seg.clippedStart || !seg.isStart;
      const continuesRight = seg.clippedEnd || !seg.isEnd;
      const partial: Omit<WeekSegment, "lane"> = {
        key: `${span.id}:${seg.weekIndex}`,
        id: span.id,
        label: span.crew ?? "",
        weekIndex: seg.weekIndex,
        startCol: seg.startCol,
        span: seg.span,
        continuesLeft,
        continuesRight,
        status: span.status,
        canceled: span.canceled,
        hasConflict: span.hasConflict,
      };
      const bucket = byWeek.get(seg.weekIndex) ?? [];
      bucket.push(partial);
      byWeek.set(seg.weekIndex, bucket);
    }
  }

  const weeks = [...byWeek.entries()]
    .sort(([a], [b]) => a - b)
    .map(([weekIndex, segs]) => {
      const laned = assignLanes(segs);
      const laneCount =
        laned.length > 0 ? Math.max(...laned.map((s) => s.lane)) + 1 : 0;
      return { weekIndex, segments: laned, laneCount };
    });

  return { weeks };
}
